// ─────────────────────────────────────────────────────────────────────────────
// REQ-109 — Fondo de estrellas animado de "Brillamos Juntos" (ShineTogetherHome).
//
// Portado de la maqueta (features/brillamos/BrillamosStars.tsx) SIN dependencias nuevas:
// canvas 2D puro + requestAnimationFrame. 220 estrellas (40% de 5 puntas, 60% puntos),
// twinkle individual con onda seno, halo/glow, regeneración al redimensionar. Puramente
// decorativo (aria-hidden) — no muestra ningún dato ni puntos.
// Nota: el parallax con el mouse del REQ-109 original se quitó — el fondo debe permanecer
// fijo en esta sección (reportado como bug: el movimiento del mouse desplazaba las estrellas).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef } from 'react';

interface Star {
  x: number; y: number;
  outerRadius: number; twinkleSpeed: number; twinklePhase: number;
  rotation: number; glowRadius: number; minAlpha: number; maxAlpha: number;
  isFivePoint: boolean;
}

const STAR_COUNT = 220;
const FIVE_POINT_RATIO = 0.4; // 40% estrellas de 5 puntas, 60% puntos circulares (spec REQ-109)

export function BrillamosStars() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const starsRef = useRef<Star[]>([]);
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    // Lavanda clara (#a78bfa, mismo acento de "brillo" que el resto del módulo — StreakWidget,
    // constelación) en vez del morado Sigma oscuro (#332F81) original: ese tono queda demasiado
    // cerca de los stops del degradado de fondo (#1a0a3e/#0d0628/#060214) y, con el alpha bajo de
    // abajo, las estrellas no se distinguían del cielo — el usuario veía el degradado liso sin
    // puntos de estrella.
    const starColor = { r: 196, g: 181, b: 253 };

    function generateStars(w: number, h: number) {
      const stars: Star[] = [];
      for (let i = 0; i < STAR_COUNT; i++) {
        const x = Math.random() * w;
        const y = Math.random() * h;
        stars.push({
          x, y,
          outerRadius: (Math.random() * 6 + 3) * dpr,
          twinkleSpeed: 0.006 + Math.random() * 0.018,
          twinklePhase: Math.random() * Math.PI * 2,
          rotation: Math.random() * Math.PI * 2,
          glowRadius: 1.0 + Math.random() * 0.8,
          // Antes 0.05-0.13 / 0.2-0.35: el pico del twinkle apenas llegaba a 0.35 de opacidad,
          // invisible contra el fondo oscuro. Subido para que el punto de la estrella se note sin
          // perder el efecto de parpadeo (el valle sigue siendo tenue).
          minAlpha: 0.15 + Math.random() * 0.1,
          maxAlpha: 0.55 + Math.random() * 0.35,
          isFivePoint: Math.random() < FIVE_POINT_RATIO,
        });
      }
      starsRef.current = stars;
    }

    function starPath(cx: number, cy: number, outerR: number, innerR: number, rotation: number) {
      const points = 5;
      ctx!.beginPath();
      for (let i = 0; i < points * 2; i++) {
        const r = i % 2 === 0 ? outerR : innerR;
        const angle = (Math.PI / points) * i - Math.PI / 2 + rotation;
        const px = cx + Math.cos(angle) * r;
        const py = cy + Math.sin(angle) * r;
        if (i === 0) ctx!.moveTo(px, py);
        else ctx!.lineTo(px, py);
      }
      ctx!.closePath();
    }

    function resize() {
      canvas!.width = canvas!.offsetWidth * dpr;
      canvas!.height = canvas!.offsetHeight * dpr;
      generateStars(canvas!.width, canvas!.height);
    }

    function draw(time: number) {
      const w = canvas!.width;
      const h = canvas!.height;
      const { r: cr, g: cg, b: cb } = starColor;
      ctx!.clearRect(0, 0, w, h);

      for (const star of starsRef.current) {
        const twinkle = Math.sin(time * star.twinkleSpeed + star.twinklePhase);
        const alpha = star.minAlpha + ((twinkle + 1) / 2) * (star.maxAlpha - star.minAlpha);
        const scale = 0.85 + twinkle * 0.15;
        const outerR = star.outerRadius * scale;
        const glowSize = outerR * star.glowRadius;

        const glow = ctx!.createRadialGradient(star.x, star.y, 0, star.x, star.y, glowSize * 2);
        glow.addColorStop(0, `rgba(${cr}, ${cg}, ${cb}, ${alpha * 0.3})`);
        glow.addColorStop(0.5, `rgba(${cr}, ${cg}, ${cb}, ${alpha * 0.08})`);
        glow.addColorStop(1, `rgba(${cr}, ${cg}, ${cb}, 0)`);
        ctx!.fillStyle = glow;
        ctx!.fillRect(star.x - glowSize * 2, star.y - glowSize * 2, glowSize * 4, glowSize * 4);

        ctx!.fillStyle = `rgba(${cr}, ${cg}, ${cb}, ${alpha})`;
        if (star.isFivePoint) {
          starPath(star.x, star.y, outerR, outerR * 0.4, star.rotation);
          ctx!.fill();
        } else {
          ctx!.beginPath();
          ctx!.arc(star.x, star.y, outerR * 0.5, 0, Math.PI * 2);
          ctx!.fill();
        }
      }
    }

    function animate() {
      timeRef.current += 1;
      draw(timeRef.current);
      rafRef.current = requestAnimationFrame(animate);
    }

    resize();
    window.addEventListener('resize', resize);
    animate();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="brillamos-stars-canvas" aria-hidden="true" />;
}

export default BrillamosStars;
